import { useState, useRef, useEffect, useCallback } from "react";
import { motion } from "framer-motion";

const CHARS = "ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789#$%&*<>/\\";

const useScramble = (target) => {
  const [text, setText] = useState(target.replace(/[^ ]/g, "█"));
  const [done, setDone] = useState(false);
  const timer = useRef(null);

  const decode = useCallback(() => {
    if (timer.current) clearInterval(timer.current);
    let frame = 0;
    timer.current = setInterval(() => {
      frame++;
      const out = target
        .split("")
        .map((ch, i) => {
          if (ch === " ") return " ";
          if (i < frame / 2) return ch;
          return CHARS[Math.floor(Math.random() * CHARS.length)];
        })
        .join("");
      setText(out);
      if (frame / 2 >= target.length) {
        clearInterval(timer.current);
        setText(target);
        setDone(true);
      }
    }, 25);
  }, [target]);

  useEffect(() => () => clearInterval(timer.current), []);
  return { text, decode, done };
};

const Line = ({ label, target, testId }) => {
  const { text, decode, done } = useScramble(target);
  return (
    <button
      data-testid={testId}
      onMouseEnter={decode}
      onClick={decode}
      className="w-full text-left flex flex-col sm:flex-row sm:items-baseline gap-1 sm:gap-6 py-3 border-b border-white/5 group"
    >
      <span className="text-[10px] uppercase tracking-[0.3em] text-[#8e8ea0] shrink-0 w-28">{label}</span>
      <span className={`text-sm sm:text-base tracking-wide transition-colors duration-300 ${done ? "text-[#00f0ff]" : "text-[#f8f9fa]/70 group-hover:text-[#f8f9fa]"}`}>
        {text}
      </span>
    </button>
  );
};

export const Transmission = () => (
  <section className="py-24 sm:py-32 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
    <motion.div
      initial={{ opacity: 0, y: 50 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-80px" }}
      transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
      data-testid="transmission-console"
      className="glow-border font-mono-z bg-[#09090d] border border-white/10 p-6 sm:p-10 rounded-lg shadow-2xl relative overflow-hidden"
    >
      <div className="flex items-center justify-between mb-8">
        <div className="flex items-center gap-2">
          <span className="w-2.5 h-2.5 rounded-full bg-[#ff3366]/70" />
          <span className="w-2.5 h-2.5 rounded-full bg-[#8e8ea0]/40" />
          <span className="w-2.5 h-2.5 rounded-full bg-[#00f0ff]/70" />
        </div>
        <span className="text-[10px] uppercase tracking-[0.35em] text-[#8e8ea0]">Intercepted // Hover to decrypt</span>
      </div>
      <Line testId="cipher-line-1" label="Origin" target="UNKNOWN COORDINATES" />
      <Line testId="cipher-line-2" label="Payload" target="SOMETHING BIG IS COMING" />
      <Line testId="cipher-line-3" label="Directive" target="REGISTER YOUR SIGNAL BELOW" />
      <Line testId="cipher-line-4" label="Signature" target="— ZAVICO" />
    </motion.div>
  </section>
);

export default Transmission;
